import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button, StatusStrip, type StatusStripStep } from '@xxtract/ui';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { usePhysicalTarget, useRefreshPhysicalTarget } from '../hooks/usePhysicalTarget';
import { useProcessenFilter } from '../hooks/useProcessenFilter';
import { formatFetchedAt } from '../format';
import type { PhysicalTargetStep } from '../processes.api';

const GEEN_EIGEN_LIJSTEN = [] as const;

/**
 * R2 — het fysieke doelbeeld: de keten van ontvangst tot retour als ladder van tegels naast elkaar,
 * per stap de producten van lopende orders die daar nu staan (spec-processen-actuele-stand).
 * De sporten komen data-gedreven uit de BFF: een stap die het processysteem nog niet bijhoudt staat
 * er zichtbaar leeg bij, zodat een blinde vlek nooit als ophoping of als knelpunt leest.
 *
 * Klant en drempel komen uit de gedeelde Processen-filter (CAP-4, spec-tabelfilters story 12); de
 * productenlijst leest dezelfde stand, zodat tegel, doelbeeld en lijst dezelfde aantallen tonen.
 */
export function FysiekDoelbeeld() {
  const { t, i18n } = useTranslation();
  // Leest het adres (oude links met `gln`) en de gedeelde Processen-filter; geen weigermelding (zie story 12).
  const { values: filterValues } = useProcessenFilter(GEEN_EIGEN_LIJSTEN);
  const targetQuery = usePhysicalTarget(filterValues.gln);
  const refreshTarget = useRefreshPhysicalTarget(filterValues.gln);
  const [isRefreshing, setIsRefreshing] = useState(false);

  if (targetQuery.isError) {
    return <ApiErrorMessage error={targetQuery.error} onRetry={() => targetQuery.refetch()} />;
  }

  const data = targetQuery.data?.data;

  async function handleRefresh() {
    setIsRefreshing(true);
    try {
      await refreshTarget();
    } finally {
      setIsRefreshing(false);
    }
  }

  /**
   * Een sport zonder telling krijgt een lege staat, maar wél de juiste reden: "niet bijgehouden"
   * (het processysteem kent de stap niet) is iets anders dan "bron onbereikbaar" (we konden even
   * niets ophalen). Beide leeg, verschillende boodschap.
   */
  function toStripStep(step: PhysicalTargetStep): StatusStripStep {
    const isCovered = step.coverageState === 'covered' && step.count != null;
    const isOffline = step.coverageState === 'publisher_offline';
    return {
      stepKey: step.key,
      // Een stap buiten de canonieke keten heeft geen vertaling; dan valt de ruwe broncode terug.
      label: i18n.exists(`processes.physical.steps.${step.key}`) ? t(`processes.physical.steps.${step.key}`) : (step.canonicalStep ?? step.key),
      count: step.count,
      coverageState: step.coverageState,
      // De reden hangt aan de stap zelf, niet aan de pagina: bij een deels bereikbare bron kunnen
      // beide redenen naast elkaar in dezelfde ladder staan.
      noDataLabel: isOffline ? t('processes.physical.stepUnavailable') : t('processes.physical.stepNotTracked'),
      statusLabel: isCovered
        ? step.count! > 0
          ? t('processes.physical.status.inProgress')
          : t('processes.physical.status.empty')
        : isOffline
          ? t('processes.physical.status.unavailable')
          : t('processes.physical.status.notTracked'),
    };
  }

  const sourceUnavailable = data?.sources.procesManagement === 'unavailable';

  return (
    <div className="space-y-6" data-testid="physical-target-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-[var(--foreground)]">{t('processes.physical.title')}</h1>
          <p className="text-xs text-[var(--foreground-muted)]">{t('processes.physical.subtitle')}</p>
          {filterValues.gln != null ? (
            <p className="text-xs font-semibold text-[var(--foreground)]" data-testid="physical-target-customer-filter">
              {t('processes.physicalItems.filteredByCustomer', { gln: filterValues.gln })}
            </p>
          ) : null}
        </div>

        {data ? (
          <div className="flex items-center gap-3">
            <p aria-live="polite" className="text-xs text-[var(--foreground-muted)]" data-testid="physical-target-updated-at">
              {t('processes.updatedAt', { time: formatFetchedAt(data.fetchedAt, i18n.language) })}
            </p>
            <Button data-testid="physical-target-refresh" disabled={isRefreshing} onClick={handleRefresh} size="sm" variant="secondary">
              <span aria-hidden="true" className="material-symbols-outlined text-[16px]">refresh</span>
              {t('processes.refresh')}
            </Button>
          </div>
        ) : null}
      </header>

      {targetQuery.isLoading ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6" data-testid="physical-target-loading">
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <div className="h-28 animate-pulse rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--background-muted)]" key={index} />
          ))}
        </div>
      ) : null}

      {data ? (
        <>
          {sourceUnavailable ? (
            <p className="text-xs text-[var(--info-text)]" data-testid="physical-target-degraded" role="status">{t('processes.degraded.title')}</p>
          ) : null}

          {/* CAP-8: bij een onbereikbare bron geen totaal — een 0 zou lezen als "niets in huis". */}
          {sourceUnavailable ? null : (
            <p className="text-xs text-[var(--foreground-muted)]" data-testid="physical-target-total">
              {t('processes.physical.totalProducts', { count: data.totalProducts })}
            </p>
          )}

          <StatusStrip ariaLabel={t('processes.physical.title')} noDataLabel={t('processes.physical.stepNotTracked')} steps={data.steps.map(toStripStep)} />

          <Link className="inline-block text-xs text-[var(--secondary)] hover:underline" data-testid="physical-items-link" to="/processes/in-huis">
            {t('processes.physicalItems.linkToItems')}
          </Link>
        </>
      ) : null}
    </div>
  );
}
